import styled from '@emotion/styled';
import { useMemo, useRef, useEffect, memo } from 'react';
import FullCalendar, {
  EventClickArg,
  DateSelectArg,
  EventInput,
  DayHeaderContentArg,
} from '@fullcalendar/react';
import { DateClickArg } from '@fullcalendar/interaction';

import {
  colorPalette,
  gray05,
  primary,
  whiteFix,
} from 'presentations/views/styles/color';
import Calendar from 'presentations/views/components/molecules/Calendar';
import {
  formatToDate,
  formatToEXDATE,
  formatToTimeShort,
  getDateTime,
} from 'infras/datetime';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  CalendarUnitType,
  CREATING_EVENT_CLASS_NAME,
  CREATING_EVENT_ID_PREFIX,
  EventDate,
} from 'presentations/reducers/DashboardCalendar';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
  zIndex,
} from 'presentations/views/styles';
import Icon from 'presentations/views/components/atoms/Icon';
import EventTypeCalendar from 'domains/models/event_type_calendar';
import { convertCalendarToRRule } from 'presentations/views/helpers/event_type_calendar';
import { EditCalendar } from '../../molecules/AvailabilityPopover/hooks';

export const calendarUnitForAvailabilityKey = 'calendarUnitForAvailability';
const MINUTES_STEP = 15;
const DEFAULT_DURATION = 60;

export interface Props {
  timeZone: string;
  calendarTimeZone: string;
  locale: string;
  viewer: ViewerItem;
  startedAt: Date;
  endedAt: Date;
  eventTypeCalendars: EventTypeCalendar[];
  selectedEventDate: EventDate | null;
  editCalendar: EditCalendar | null;
  withoutContainerBorder?: boolean;
  onClickEvent: (
    calendar: EventTypeCalendar,
    start: Date | null,
    element: HTMLElement,
  ) => void;
  onSelect: (eventDate: EventDate) => void;
  startWeekDay?: number;
  calendarUnit: CalendarUnitType;
  initialView: 'dayGridMonth' | 'timeGridWeek';
}

const Component = memo((props: Props) => {
  const fullCalendarRef = useRef<FullCalendar>(null);
  const fullCalendarApi = fullCalendarRef.current?.getApi();

  useEffect(() => {
    switch (props.calendarUnit) {
      case 'month':
        fullCalendarApi?.changeView('dayGridMonth');
        break;
      case 'week':
        fullCalendarApi?.changeView('timeGridWeek');
        break;
    }
  }, [props.calendarUnit]);

  useEffect(() => {
    fullCalendarApi?.gotoDate(props.startedAt);
  }, [props.startedAt]);

  useEffect(() => {
    if (!props.selectedEventDate || !props.selectedEventDate.start) return;

    fullCalendarApi?.scrollToTime(
      `${props.selectedEventDate.start.getHours()}:00`,
    );
  }, [props.selectedEventDate]);

  const eventClick = async (args: EventClickArg) => {
    // 候補日についてはクリックイベントは無視
    if (args.event.id.startsWith(CREATING_EVENT_ID_PREFIX)) {
      return;
    }

    // カレンダー編集を開く
    const { calendar } = args.event.extendedProps;
    props.onClickEvent(calendar, args.event.start, args.el);
  };

  const dateClick = (info: DateClickArg) => {
    if (!info.allDay) {
      const start = getDateTime(info.date, props.calendarTimeZone);
      let end = getDateTime(info.date, props.calendarTimeZone).plus({
        minutes: DEFAULT_DURATION,
      });
      if (!start.hasSame(end, 'day')) {
        end = start.set({ hour: 24, minute: 0 });
      }
      fullCalendarApi?.select(info.date, end.toJSDate());
    } else {
      const current = getDateTime(undefined, props.calendarTimeZone);
      const start = getDateTime(info.date, props.calendarTimeZone).set({
        hour: current.hour,
        minute:
          current.minute + (MINUTES_STEP - (current.minute % MINUTES_STEP)),
      });
      const end = start.plus({
        minutes: DEFAULT_DURATION,
      });

      props.onSelect({
        id: `${CREATING_EVENT_ID_PREFIX}${new Date().getTime()}`,
        start: start.toJSDate(),
        end: end.toJSDate(),
        isAllday: false,
      });
      fullCalendarApi?.unselect();
    }
  };

  const calendarSelect = (info: DateSelectArg) => {
    if (!info.allDay) {
      const start = getDateTime(info.start, props.calendarTimeZone);
      const end = getDateTime(info.end, props.calendarTimeZone);

      props.onSelect({
        id: `${CREATING_EVENT_ID_PREFIX}${new Date().getTime()}`,
        start: start.toJSDate(),
        end: end.toJSDate(),
        isAllday: false,
      });
    }
    fullCalendarApi?.unselect();
  };

  const dayHeaderContent = (args: DayHeaderContentArg) => {
    const weekdayText =
      props.initialView === 'dayGridMonth'
        ? getDateTime(args.date, props.calendarTimeZone).toUTC().toFormat('EEE')
        : getDateTime(args.date, props.calendarTimeZone).toFormat('EEE');
    return (
      <WeekDayHeader>
        <WeekDayHeaderDate today={args.isToday}>
          {args.date.getDate()}
        </WeekDayHeaderDate>
        <WeekDayHeaderWeekday>{weekdayText}</WeekDayHeaderWeekday>
      </WeekDayHeader>
    );
  };

  const utcOffset = useMemo(() => {
    return (
      getDateTime().toUTC().offset -
      getDateTime(undefined, props.calendarTimeZone).offset
    );
  }, [props.calendarTimeZone]);

  let events: EventInput[] = [];

  // RRuleと組み合わせるため、少し幅を取った表示範囲とする
  const startedAt = getDateTime(props.startedAt, props.calendarTimeZone)
    .plus({
      minute: utcOffset,
    })
    .toJSDate();
  const endedAt = getDateTime(props.endedAt, props.calendarTimeZone)
    .plus({
      minute: -utcOffset,
    })
    .toJSDate();

  props.eventTypeCalendars.forEach((calendar) => {
    const rrule = convertCalendarToRRule(calendar, props.calendarTimeZone);
    if (rrule) {
      // 繰り返し
      const rruleEvents: EventInput[] = rrule
        .between(startedAt, endedAt)
        .filter((d) => {
          const start = getDateTime(d, props.calendarTimeZone)
            .plus({
              minute: utcOffset,
            })
            .set({
              hour: calendar.startHour,
              minute: calendar.startMinute,
              second: 0,
            });
          return Array.isArray(calendar.exceptions)
            ? !calendar.exceptions.includes(formatToEXDATE(start))
            : true;
        })
        .filter((d) => {
          const date = getDateTime(d, props.calendarTimeZone).plus({
            minute: utcOffset,
          });
          const start = getDateTime(date).set({
            hour: calendar.startHour,
            minute: calendar.startMinute,
          });
          return (
            start.toMillis() >= props.startedAt.getTime() &&
            start.toMillis() < props.endedAt.getTime()
          );
        })
        .map<EventInput>((d) => {
          const date = getDateTime(d, props.calendarTimeZone).plus({
            minute: utcOffset,
          });
          const start = getDateTime(date).set({
            hour: calendar.startHour,
            minute: calendar.startMinute,
          });
          const end = getDateTime(date).set({
            hour: calendar.endHour,
            minute: calendar.endMinute,
          });
          const isShortEvent = end.diff(start, 'hour').hours < 1;
          const isEditingCalendar =
            props.editCalendar?.calendar.id === calendar.id &&
            start.hasSame(
              getDateTime(props.editCalendar.date, props.calendarTimeZone),
              'day',
            );
          return {
            title: `${formatToTimeShort(
              start,
              props.calendarTimeZone,
            )}-${formatToTimeShort(end, props.calendarTimeZone)}`,
            start: start.toJSDate(),
            end: end.toJSDate(),
            borderColor: colorPalette.primary.www,
            backgroundColor: colorPalette.blueGrayPale.www,
            className: `availability ${isShortEvent ? ' short-event' : ''} ${
              isEditingCalendar ? CREATING_EVENT_CLASS_NAME : ''
            }`,
            extendedProps: {
              calendar,
              icon: 'repeat',
            },
          };
        });

      // event.lengthが0の場合とは繰り返さないEventTypeCalendar または 繰り返しEventTypeCalendarで月の最終週の場合
      if (rruleEvents.length === 0 && !calendar.frequencyType) {
        // 画面で選択しているタイムゾーンより遅れている場合は、繰り返しなしのfullcalendarでeventの表示が前日の日付になってしまうのを防ぐ
        const selectedTimeZoneUtcOffset =
          getDateTime().toUTC().offset -
          getDateTime().setZone(props.timeZone).offset;
        const minuteOffset =
          selectedTimeZoneUtcOffset < utcOffset ? 60 * 24 : 0;
        const date = getDateTime(
          rrule.options.dtstart,
          props.calendarTimeZone,
        ).plus({
          minute: utcOffset + minuteOffset,
        });
        const start = getDateTime(date).set({
          hour: calendar.startHour,
          minute: calendar.startMinute,
        });
        const end = getDateTime(date).set({
          hour: calendar.endHour,
          minute: calendar.endMinute,
        });
        const isShortEvent = end.diff(start, 'hour').hours < 1;
        const isEditingCalendar =
          props.editCalendar?.calendar.id === calendar.id;
        events = events.concat({
          title: `${formatToTimeShort(
            start,
            props.calendarTimeZone,
          )}-${formatToTimeShort(end, props.calendarTimeZone)}`,
          start: start.toJSDate(),
          end: end.toJSDate(),
          borderColor: colorPalette.primary.www,
          backgroundColor: colorPalette.blueGrayPale.www,
          className: `availability ${isShortEvent ? ' short-event' : ''} ${
            isEditingCalendar ? CREATING_EVENT_CLASS_NAME : ''
          }`,
          extendedProps: {
            calendar,
          },
        });
      } else {
        events = events.concat(rruleEvents);
      }
    } else {
      const start = getDateTime(calendar.startedAt, props.calendarTimeZone).set(
        {
          hour: calendar.startHour,
          minute: calendar.startMinute,
        },
      );
      const end = getDateTime(calendar.startedAt, props.calendarTimeZone).set({
        hour: calendar.endHour,
        minute: calendar.endMinute,
      });

      if (
        start.toMillis() < props.startedAt.getTime() ||
        start.toMillis() >= props.endedAt.getTime()
      ) {
        return;
      }

      const isShortEvent = end.diff(start, 'hour').hours < 1;
      const isEditingCalendar = props.editCalendar?.calendar.id === calendar.id;
      events = events.concat({
        title: `${formatToTimeShort(
          start,
          props.calendarTimeZone,
        )}-${formatToTimeShort(end, props.calendarTimeZone)}`,
        start: start.toJSDate(),
        end: end.toJSDate(),
        borderColor: colorPalette.primary.www,
        backgroundColor: colorPalette.blueGrayPale.www,
        className: `availability ${isShortEvent ? ' short-event' : ''} ${
          isEditingCalendar ? CREATING_EVENT_CLASS_NAME : ''
        }`,
        extendedProps: {
          calendar,
        },
      });
    }
  });

  // 候補日時
  if (props.selectedEventDate) {
    const date = props.selectedEventDate;
    const start = getDateTime(date.start, props.calendarTimeZone);
    let end = getDateTime(date.end, props.calendarTimeZone);
    if (date.isAllday) {
      end = getDateTime(date.end, props.calendarTimeZone)
        .plus({ day: 1 })
        .startOf('day');
    }
    let title: string;
    if (date.isAllday) {
      const startString = formatToDate(date.start, props.calendarTimeZone);
      const endString = formatToDate(date.end, props.calendarTimeZone);
      title = `${startString}${
        startString !== endString ? `-${endString}` : ''
      }`;
    } else {
      title = `${formatToTimeShort(
        date.start,
        props.calendarTimeZone,
      )}-${formatToTimeShort(date.end, props.calendarTimeZone)}`;
    }

    events = events.concat({
      start: start.toJSDate(),
      end: end.toJSDate(),
      allDay: date.isAllday,
      title,
      borderColor: colorPalette.primary.www,
      backgroundColor: colorPalette.white.www,
      className: CREATING_EVENT_CLASS_NAME,
      editable: true,
      id: date.id,
    });
  }

  const constraint = {
    startTime: '0:00',
    endTime: '24:00',
  };

  return (
    <Container>
      <Calendar
        // 全体設定
        locale={props.locale}
        timeZone={props.calendarTimeZone}
        fullCalendarRef={fullCalendarRef}
        initialView={props.initialView}
        eventClick={eventClick}
        initialDate={props.startedAt}
        eventSources={[{ events }]}
        eventContent={(args: any, createElement: any) => {
          const title = args.event._def.title;
          const icon = args.event._def.extendedProps.icon;
          return (
            <NormalEvent>
              <EventTitle className="fc-event-title">
                {icon && <TitleIcon name={String(icon)} />}
                {title}
              </EventTitle>
            </NormalEvent>
          );
        }}
        expandRows={false}
        dayHeaderContent={
          props.calendarUnit === 'month' ? undefined : dayHeaderContent
        }
        allDaySlot={false}
        startWeekDay={props.startWeekDay}
        // 選択
        selectable
        dateClick={dateClick}
        select={calendarSelect}
        selectConstraint={constraint}
        eventConstraint={constraint}
        selectAllow={(selectInfo) => !selectInfo.allDay}
        styleProps={{
          withoutContainerBorder: props.withoutContainerBorder ?? true,
        }}
      />
    </Container>
  );
});

export default Component;

const Container = styled.div`
  height: 100%;

  & .fc .fc-timegrid-slot-lane {
    cursor: cell;
  }

  & .fc .fc-timegrid-divider {
    background-color: ${gray05};
    padding: 0;
  }

  & .fc .fc-timegrid .fc-col-header-cell-cushion {
    padding: 0 !important;
  }

  & .fc .fc-timegrid-event.availability {
    padding: 0 2px;
    border: none;
    border: 2px dotted ${primary};
    border-radius: ${borderRadius(3)};
    color: ${primary};
  }
  & .fc .fc-timegrid-event.availability .fc-event-title {
    color: ${primary};
    word-break: break-all;
    white-space: pre;
  }

  & .fc .fc-daygrid-event.availability.creating-event {
    padding: 0;
    border: none;
    color: ${primary};
  }

  & .fc .fc-popover {
    z-index: ${zIndex(1200)};
  }
`;

const NormalEvent = styled.div``;
const EventTitle = styled.div`
  display: flex;
  align-items: center;
`;
const TitleIcon = styled(Icon)`
  width: 16px !important;
  height: 16px !important;
  margin-right: 3px;
  flex-shrink: 0;
`;

const WeekDayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 43px;

  ${mediaMax.tablet} {
    flex-shrink: 0;
    width: 100px;
  }
`;

const WeekDayHeaderDate = styled.div<{
  today: boolean;
}>`
  width: 28px;
  height: 28px;
  margin-right: ${spacing(4)};
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  line-height: 28px;
  vertical-align: middle;
  border-radius: ${({ today }) => (today ? '50%' : 'initial')};
  background-color: ${({ today }) => (today ? primary : 'initial')};
  color: ${({ today }) => (today ? whiteFix : 'inherit')};

  ${mediaMax.tablet} {
    width: 20px;
    height: 20px;
    font-size: ${fontSize(10)};
    line-height: 20px;
    text-align: center;
  }
`;
const WeekDayHeaderWeekday = styled.div`
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    font-size: ${fontSize(10)};
  }
`;
